/**
 * "Review & refine" — shown AFTER compose and BEFORE anything posts. The member sees the AI draft,
 * can edit it, switch voice + "Rewrite" (re-composes), and sees a faithful preview of the exact
 * Discord embed that will post. "Post it" confirms; "Back" returns to the form.
 */
import type { ReviewPayload, RatingValue } from '@pghbites/shared';
import { Header } from './Header.js';
import { VoicePicker } from './VoicePicker.js';
import { EmbedPreview } from './EmbedPreview.js';
import { LegalFooter } from './LegalFooter.js';

interface RefineScreenProps {
  review: ReviewPayload;
  draft: string;
  rewriting: boolean;
  posting: boolean;
  error?: string | null;
  onDraftChange: (text: string) => void;
  onVoiceChange: (voice: string) => void;
  onRewrite: () => void;
  onPost: () => void;
  onBack: () => void;
}

export function RefineScreen({
  review, draft, rewriting, posting, error,
  onDraftChange, onVoiceChange, onRewrite, onPost, onBack,
}: RefineScreenProps) {
  const busy = rewriting || posting;
  const rating = (review.rating ?? '2') as RatingValue;

  return (
    <div className="screen">
      <div className="screen-scroll">
        <Header />

        <section className="block">
          <h2 className="block-title">Review &amp; refine</h2>
          <p className="block-hint">Here’s your review. Edit anything, switch the voice, or rewrite it. Nothing posts until you tap <strong>Post it</strong>.</p>
          {/* AI-output disclosure (legal item 9). */}
          <p className="ai-disclosure">✨ This draft was composed by AI from your input. Edit it to make it yours.</p>

          <label className="field" htmlFor="refine-draft">
            <span className="field-label">Your review</span>
            <textarea
              id="refine-draft"
              className="field-input refine-draft"
              value={draft}
              rows={6}
              maxLength={1200}
              disabled={rewriting}
              onChange={(e) => onDraftChange(e.target.value)}
            />
          </label>

          <div className="refine-rewrite">
            <div className="refine-voice">
              <VoicePicker value={review.voice} onChange={onVoiceChange} />
            </div>
            <button type="button" className="btn btn-secondary refine-rewrite-btn" disabled={busy} onClick={onRewrite}>
              {rewriting ? 'Rewriting…' : '↻ Rewrite'}
            </button>
          </div>
        </section>

        <section className="block">
          <h2 className="block-title">This is what will post</h2>
          <EmbedPreview
            restaurant={review.restaurant}
            rating={rating}
            voice={review.voice}
            draft={draft}
            photoUrl={review.photo_url}
          />
        </section>

        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}

        <LegalFooter showAcceptance />

        <div className="screen-spacer" />
      </div>

      <div className="submit-bar">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || !draft.trim()} onClick={onPost}>
          {posting ? 'Posting…' : 'Post it'}
        </button>
      </div>
    </div>
  );
}
